import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { DialogFooter } from '@/components/ui/dialog';

export interface ObraGasto {
  id?: string;
  obra_id: string;
  empresa_id?: string;
  tipo: string;
  descripcion: string;
  proveedor?: string | null;
  fecha: string;
  importe: number;
  iva_porcentaje?: number | null;
  notas?: string | null;
}

export const TIPOS_GASTO = [
  { value: 'alquiler_maquinaria', label: 'Alquiler de maquinaria' },
  { value: 'subcontratacion', label: 'Subcontratación' },
  { value: 'imprevisto', label: 'Imprevisto' },
  { value: 'dietas', label: 'Dietas' },
  { value: 'desplazamiento', label: 'Desplazamiento' },
  { value: 'otros', label: 'Otros' },
];

interface Props {
  obraId: string;
  gasto?: ObraGasto | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export function ObraGastoForm({ obraId, gasto, onSuccess, onCancel }: Props) {
  const { user, perfil } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ObraGasto>({
    obra_id: obraId,
    tipo: 'otros',
    descripcion: '',
    proveedor: '',
    fecha: new Date().toISOString().slice(0, 10),
    importe: 0,
    iva_porcentaje: 21,
    notas: '',
  });

  useEffect(() => {
    if (gasto) setForm({ ...gasto, proveedor: gasto.proveedor || '', notas: gasto.notas || '' });
  }, [gasto]);

  const handleSave = async () => {
    if (!user || !perfil?.empresa_id) return;
    if (!form.descripcion.trim() || !form.importe || form.importe <= 0) {
      toast({ title: 'Datos incompletos', description: 'Descripción e importe son obligatorios', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        empresa_id: perfil.empresa_id,
        obra_id: obraId,
        tipo: form.tipo,
        descripcion: form.descripcion.trim(),
        proveedor: form.proveedor?.trim() || null,
        fecha: form.fecha,
        importe: Number(form.importe),
        iva_porcentaje: form.iva_porcentaje != null ? Number(form.iva_porcentaje) : null,
        notas: form.notas?.trim() || null,
      };
      if (gasto?.id) {
        const { error } = await (supabase.from('obra_gastos' as any) as any)
          .update(payload).eq('id', gasto.id);
        if (error) throw error;
        toast({ title: 'Gasto actualizado' });
      } else {
        payload.creado_por = user.id;
        const { error } = await (supabase.from('obra_gastos' as any) as any).insert(payload);
        if (error) throw error;
        toast({ title: 'Gasto añadido' });
      }
      onSuccess();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Tipo de gasto *</Label>
          <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIPOS_GASTO.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Fecha *</Label>
          <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Descripción *</Label>
        <Input
          value={form.descripcion}
          onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
          placeholder="Ej. Alquiler dumper 3 días"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Proveedor</Label>
          <Input
            value={form.proveedor || ''}
            onChange={(e) => setForm({ ...form, proveedor: e.target.value })}
            placeholder="Opcional"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-2">
            <Label>Importe (€) *</Label>
            <Input
              type="number" step="0.01" min="0"
              value={form.importe || ''}
              onChange={(e) => setForm({ ...form, importe: Number(e.target.value) })}
            />
          </div>
          <div className="space-y-2">
            <Label>% IVA</Label>
            <Input
              type="number" step="0.01" min="0"
              value={form.iva_porcentaje ?? ''}
              onChange={(e) => setForm({ ...form, iva_porcentaje: e.target.value === '' ? null : Number(e.target.value) })}
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Notas</Label>
        <Textarea
          rows={3}
          value={form.notas || ''}
          onChange={(e) => setForm({ ...form, notas: e.target.value })}
          placeholder="Opcional"
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={handleSave} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
      </DialogFooter>
    </div>
  );
}
